import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertIcon, AccountIcon, BanIcon, MoreIcon, UndoIcon } from '../components/Brand/Icon';
import ConfirmationPanel from '../components/ConfirmationPanel';
import message from '../components/Brand/brandMessage';
import { updateMessageLayout } from '../components/Brand/MessageLayout';
import { getUser } from '../utils/func';
import useSessionIdentity, { getSessionIdentity, subscribeSessionChanges } from '../hooks/useSessionIdentity';
import url from '../utils/url';
import { changeBlock, loadBlockState } from './store';
import { useBlockState } from './hooks';
import './blocking.scss';

function ActionMenu({ children, onClose, label }) {
  const panel = useRef(null), closing = useRef(onClose);
  closing.current = onClose;
  useLayoutEffect(() => { updateMessageLayout(); }, []);
  useEffect(() => {
    const previous = document.activeElement;
    const menu = panel.current;
    panel.current?.querySelector('button:not(:disabled)')?.focus({ preventScroll: true });
    const key = event => {
      if (event.isComposing) return;
      if (event.key === 'Escape') { event.preventDefault(); closing.current(); }
      if (event.key === 'Tab' || event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const buttons = [...panel.current.querySelectorAll('button:not(:disabled)')];
        const index = buttons.indexOf(document.activeElement), backwards = event.shiftKey || event.key === 'ArrowUp';
        buttons[(index + (backwards ? -1 : 1) + buttons.length) % buttons.length]?.focus();
      }
    };
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('keydown', key);
      // The confirmation may already own focus when this menu unmounts.
      if (previous?.isConnected && (document.activeElement === document.body || menu?.contains(document.activeElement))) {
        previous.focus({ preventScroll: true });
      }
    };
  }, []);
  return <div className="user-actions-overlay" onClick={event => { if (event.target === event.currentTarget) onClose(); }}>
    <div ref={panel} className="user-actions-menu" role="dialog" aria-modal="true" aria-label={label}>{children}</div>
  </div>;
}

function BlockConfirmation({ anchor, returnFocus, children }) {
  const panel = useRef(null);
  useLayoutEffect(() => {
    const dialog = panel.current;
    let frame, disposed = false;
    const update = () => {
      frame = null;
      if (disposed || !panel.current || !anchor?.isConnected) return;
      const rect = anchor.getBoundingClientRect();
      if (rect.height > 0) panel.current.style.setProperty('--user-block-top', `${rect.bottom + 8}px`);
    };
    const schedule = () => { if (!disposed && !frame) frame = requestAnimationFrame(update); };
    const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : null;
    // The author can move without changing its own height (e.g. a title wraps
    // or the safe-area padding changes). Observe those preceding frames too.
    const header = anchor?.closest('.app-layout, .reaction-line')?.querySelector('header.header');
    const title = anchor?.closest('.current-news')?.querySelector('.storyTitle');
    new Set([anchor, header, title].filter(Boolean)).forEach(node => resize?.observe(node, { box: 'border-box' }));
    update();
    window.addEventListener('resize', schedule);
    document.addEventListener('scroll', schedule, true);
    const viewport = window.visualViewport;
    viewport?.addEventListener('resize', schedule);
    viewport?.addEventListener('scroll', schedule);
    document.fonts?.ready.then(schedule);
    return () => {
      disposed = true; cancelAnimationFrame(frame); resize?.disconnect();
      window.removeEventListener('resize', schedule);
      document.removeEventListener('scroll', schedule, true);
      viewport?.removeEventListener('resize', schedule);
      viewport?.removeEventListener('scroll', schedule);
      if (returnFocus?.isConnected && (document.activeElement === document.body || dialog?.contains(document.activeElement))) {
        returnFocus.focus({ preventScroll: true });
      }
    };
  }, [anchor, returnFocus]);
  return <div ref={panel} className="user-block-confirm">{children}</div>;
}

export default function UserActions({ user, reactionId, storyId, reportIcon = false }) {
  const { t } = useTranslation('blocking');
  const navigate = useNavigate(), location = useLocation();
  const identity = useSessionIdentity();
  const state = useBlockState(user?.id);
  const [view, setView] = useState(null);
  const trigger = useRef(null);
  const mounted = useRef(true), boundary = useRef(null);
  useEffect(() => {
    mounted.current = true;
    const pop = () => {
      const active = boundary.current;
      if (!active || window.history.state?.wiwUserActions === active.token) return;
      boundary.current = null;
      setView(null);
      if (active.after) queueMicrotask(active.after);
    };
    window.addEventListener('popstate', pop);
    return () => {
      mounted.current = false;
      window.removeEventListener('popstate', pop);
      const active = boundary.current;
      boundary.current = null;
      if (active && !active.closing && window.history.state?.wiwUserActions === active.token) { active.closing = true; window.history.back(); }
    };
  }, []);
  useEffect(() => {
    setView(null);
    const active = boundary.current;
    if (active && active.identity !== identity) {
      boundary.current = null;
      if (!active.closing && window.history.state?.wiwUserActions === active.token) { active.closing = true; window.history.back(); }
    }
  }, [user?.id, location.key, identity]);
  if (!user?.id || getUser()?.id === user.id) return null;
  const dismiss = after => {
    const active = boundary.current;
    if (active?.closing) return;
    if (active && window.history.state?.wiwUserActions === active.token) {
      active.closing = true;
      active.after = after;
      window.history.back();
    } else { boundary.current = null; setView(null); after?.(); }
  };
  const close = () => { if (!state.busy) dismiss(); };
  const change = async blocked => {
    if (identity !== getSessionIdentity()) return;
    try {
      await changeBlock(user.id, blocked);
      if (identity !== getSessionIdentity()) return;
      if (mounted.current) dismiss();
      if (blocked) {
        // The current feed card may disappear after a successful block. This
        // application-level notice and its undo must survive that unmount.
        let unsubscribe = () => {};
        const notice = message.success({ content: `${t('blockedStatus')} · @${user.user_name || user.id}`, duration: 8,
          onClose: () => unsubscribe(), action: { label: t('unblockUser'), icon: <UndoIcon style={{ color: '#0AA555' }} />,
            onClick: async () => {
              if (identity !== getSessionIdentity()) return;
              try { await changeBlock(user.id, false); }
              catch { if (identity === getSessionIdentity()) message.error(t('unblockError')); }
            } } });
        unsubscribe = subscribeSessionChanges(() => { if (identity !== getSessionIdentity()) notice?.close?.(); });
      }
    }
    catch { if (mounted.current && identity === getSessionIdentity()) message.error(t(blocked ? 'blockError' : 'unblockError')); }
  };
  const report = userTarget => {
    if (identity !== getSessionIdentity()) return;
    dismiss(() => {
      if (identity !== getSessionIdentity()) return;
      navigate(userTarget ? url(`/user/id/${user.id}/report`) : url(`/opinion/${reactionId}/report`), {
      state: { storyId, username: user.user_name, from: location.pathname + location.search },
    });
    });
  };
  const open = () => {
    if (!getUser()?.id) { navigate(`${url('/login')}?from=${encodeURIComponent(location.pathname + location.search)}`); return; }
    if (boundary.current) return;
    const token = `${Date.now()}:${Math.random()}`;
    boundary.current = { token, identity };
    window.history.pushState({ ...window.history.state, wiwUserActions: token }, '');
    loadBlockState(user.id, true); setView('menu');
  };
  return <>
    <button ref={trigger} type="button" className={`wiw-icon-button user-actions-trigger${reportIcon ? ' reaction-author-report' : ''}`}
      aria-label={t('moreActions')} aria-expanded={Boolean(view)} aria-haspopup="dialog" onClick={open}>
      <span className="user-actions-trigger-face">{reportIcon ? <AlertIcon className="nav-icon" /> : <MoreIcon />}</span>
    </button>
    {view && createPortal(view === 'confirm' ? <BlockConfirmation
      returnFocus={trigger.current}
      anchor={trigger.current?.closest('.post-user') || trigger.current?.closest('header.header')}>
      <ConfirmationPanel action="block" highlight={reportIcon ? '.current-news .post-user' : '.user-profile-page header.header'}
        question={<>{t('blockQuestion')}<br /><strong>@{user.user_name || user.first_name || user.id}</strong><br />{t('blockDescription')}</>}
        onCancel={close} onOK={() => change(true)} busy={Boolean(state.busy)}
        cancelLabel={t('cancel')} confirmLabel={t('blockConfirm')} />
    </BlockConfirmation> : <ActionMenu onClose={close} label={t('moreActions')}>
      {reactionId > 0 && <button type="button" onClick={() => report(false)}><AlertIcon /><span>{t('reportComment')}</span></button>}
      <button type="button" onClick={() => report(true)}><AccountIcon /><span>{t('reportUser')}</span></button>
      {state.error && !state.loaded ? <button type="button" onClick={() => loadBlockState(user.id, true)}><UndoIcon /><span>{t('stateError')}</span></button>
        : <button type="button" disabled={Boolean(state.busy) || !state.loaded} aria-busy={Boolean(state.busy || state.loading)}
          className={state.blocked_by_viewer ? 'user-unblock' : 'user-block'}
          onClick={() => state.blocked_by_viewer ? change(false) : setView('confirm')}>
          {state.blocked_by_viewer ? <UndoIcon /> : <BanIcon />}<span>{t(state.blocked_by_viewer ? 'unblockUser' : 'blockUser')}</span>
        </button>}
    </ActionMenu>, document.body)}
  </>;
}

export function BlockedProfileStatus({ userId }) {
  const identity = useSessionIdentity();
  const state = useBlockState(userId), { t } = useTranslation('blocking');
  if (!state.blocked_by_viewer) return null;
  const unblock = async () => {
    if (identity !== getSessionIdentity()) return;
    try { await changeBlock(userId, false); }
    catch { if (identity === getSessionIdentity()) message.error(t('unblockError')); }
  };
  return <div className="blocked-profile-status">
    <span role="status"><BanIcon />{t('blockedStatus')}</span>
    <button type="button" className="user-unblock" disabled={Boolean(state.busy)} aria-busy={Boolean(state.busy)} onClick={unblock}>
      <UndoIcon /><span>{t('unblockUser')}</span>
    </button>
  </div>;
}
